'use client'

/**
 * Shared error-boundary chrome for route-level Next.js error boundaries
 * (Issue #5466).
 *
 * Consumed by `src/components/route-error-boundary.tsx` (#11651), which
 * owns the wiring — the reporting `useEffect` and the `onCopyFailure`
 * toast — for every real `error.tsx` route segment. This component owns
 * only the presentational chrome: icon bubble, h1 heading, body, action
 * row, and the copy-to-clipboard error-details panel, plus the
 * `announceAndFocus`-gated live region around the heading + message. It
 * is also consumed directly (not through `RouteErrorBoundary`) by the
 * `error-boundary-card` `component-tests` showcase page; the
 * `route-error-boundary` showcase page reaches it indirectly, through
 * `RouteErrorBoundary`.
 *
 * `src/app/global-error.tsx` is intentionally NOT a consumer (#5544 —
 * global-error must have zero `@/components` imports).
 */

import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import Link from 'next/link'
import { AlertTriangle, Check, Copy, Home, RefreshCw } from '@/components/icons'

type Tone = 'red' | 'amber'

const TONE_CLASSES: Record<Tone, { bubble: string; icon: string }> = {
  red: {
    bubble: 'bg-status-red-bg',
    icon: 'text-status-red-text',
  },
  amber: {
    bubble: 'bg-status-amber-bg',
    icon: 'text-status-amber-text',
  },
}

interface ErrorBoundaryCardProps {
  tone: Tone
  title: string
  message: ReactNode
  homeHref: string
  homeLabel: string
  error: Error & { digest?: string }
  onReset: () => void
  /** Label of the primary action. Defaults to "Try again". */
  resetLabel?: string
  /**
   * Optional hook fired when `navigator.clipboard.writeText` rejects.
   * Boundaries pass this to surface a `toastError` so a copy failure is
   * never silent (admin already did this; root + people did not).
   */
  onCopyFailure?: (err: unknown) => void
  /**
   * Whether this instance owns the assertive `role="alert"` live region —
   * scoped to just the `<h1>` heading and body message, not the action
   * row or the details panel, per WAI-ARIA APG (an `alert` region must not
   * contain focusable elements) — and steals focus to its "Try again"
   * button on mount. Defaults to `true`, which is correct for every real
   * production usage (each `error.tsx` route segment mounts exactly one
   * instance via `RouteErrorBoundary`). Set to `false` only when a view
   * intentionally mounts multiple `ErrorBoundaryCard` instances
   * simultaneously (e.g. a showcase grid) — with several `true` instances
   * mounted at once they'd compete for the same assertive live region and
   * the last-mounted one would win the focus-steal, stomping on whichever
   * instance focused first. The visible focus-ring styling on the "Try
   * again" button (both `focus-visible:ring-2` for keyboard/AT focus and a
   * plain `focus:ring-2` so the mount-time programmatic focus below is
   * also visible after a pointer interaction) is independent of this prop
   * and always applies.
   */
  announceAndFocus?: boolean
  /** `data-testid` for the card's root element. */
  testId?: string
}

export function ErrorBoundaryCard({
  tone,
  title,
  message,
  homeHref,
  homeLabel,
  error,
  onReset,
  resetLabel = 'Try again',
  onCopyFailure,
  announceAndFocus = true,
  testId,
}: ErrorBoundaryCardProps) {
  const toneClasses = TONE_CLASSES[tone]
  // Move focus to the primary action on mount so keyboard/AT users land
  // somewhere useful instead of the page silently re-rendering underneath
  // them. This is the single focus-management side effect for the card;
  // it pairs with the `role="alert"` live region below (which announces
  // the error), not a second live region. Both are gated behind
  // `announceAndFocus` so multi-instance views can opt out of both at once
  // (see prop doc above).
  const resetButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!announceAndFocus) return
    resetButtonRef.current?.focus()
  }, [announceAndFocus])

  return (
    <div
      data-testid={testId}
      className="flex items-center justify-center min-h-[60vh] p-6"
    >
      <div className="max-w-md w-full bg-surface-elevated rounded-xl shadow-sm border border-stroke-regular p-6 text-center">
        <div
          data-tone-bubble
          className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${toneClasses.bubble}`}
        >
          <AlertTriangle className={`w-6 h-6 ${toneClasses.icon}`} />
        </div>

        {/* `role="alert"` is scoped to just the heading + message — NOT
            the outer card — because WAI-ARIA APG says an `alert` region
            must not contain focusable elements, and the action row below
            (the "Try again" button + home link) plus the details `<details>`
            panel are all focusable. */}
        <div role={announceAndFocus ? 'alert' : undefined}>
          <h1 className="text-lg font-semibold text-text-primary mb-2">
            {title}
          </h1>

          <p className="text-sm text-text-secondary mb-6">{message}</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            ref={resetButtonRef}
            type="button"
            onClick={onReset}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 pointer-coarse:min-h-[44px] bg-action-primary hover:bg-action-primary-hover text-white-static rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-action-primary focus:ring-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-primary focus-visible:ring-offset-2"
          >
            <RefreshCw className="w-4 h-4" />
            {resetLabel}
          </button>

          <Link
            href={homeHref}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 pointer-coarse:min-h-[44px] bg-fill-10 hover:bg-fill-10 text-text-primary rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-primary focus-visible:ring-offset-2"
          >
            <Home className="w-4 h-4" />
            {homeLabel}
          </Link>
        </div>

        <ErrorDetails error={error} onCopyFailure={onCopyFailure} />
      </div>
    </div>
  )
}

const COPY_CONFIRMATION_DURATION_MS = 2000

interface ErrorDetailsProps {
  error: Error & { digest?: string }
  onCopyFailure?: (err: unknown) => void
}

export function ErrorDetails({ error, onCopyFailure }: ErrorDetailsProps) {
  const [copied, setCopied] = useState(false)
  // Hold the confirmation timeout id so we can clear it on unmount and on
  // a rapid second click — without this, a click → "Try again" within 2s
  // calls setCopied on an unmounted component, and two clicks in quick
  // succession stack timers so the first one prematurely flips `copied`
  // back to false while the user can still see the second confirmation.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const errorText = [
    error.message,
    error.digest ? `\nDigest: ${error.digest}` : '',
    error.stack ? `\n${error.stack}` : '',
  ].join('')

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(errorText)
      setCopied(true)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        setCopied(false)
        timeoutRef.current = null
      }, COPY_CONFIRMATION_DURATION_MS)
    } catch (err) {
      onCopyFailure?.(err)
    }
  }

  return (
    <details className="mt-6 text-left">
      <summary className="text-xs text-text-secondary cursor-pointer hover:text-text-primary">
        Error details
      </summary>
      <div className="relative mt-2">
        <button
          type="button"
          onClick={handleCopy}
          className="absolute top-2 right-2 p-1.5 rounded-md bg-surface-elevated hover:bg-fill-10 border border-stroke-regular text-text-secondary hover:text-text-primary transition-colors"
          aria-label="Copy error details"
        >
          {copied ? (
            <Check className="w-3.5 h-3.5 text-status-green-text" />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </button>
        <pre className="p-3 bg-status-red-bg rounded-lg text-xs text-status-red-text overflow-auto max-h-40">
          {errorText}
        </pre>
      </div>
    </details>
  )
}
